<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'

export interface SelectOption {
    value: string
    label: string
}

const props = withDefaults(defineProps<{
    modelValue: string
    options: SelectOption[]
    placeholder?: string
}>(), {
    placeholder: 'Select...'
})

const emit = defineEmits<{
    'update:modelValue': [value: string]
    'change': [value: string]
}>()

const isOpen = ref(false)
const dropdownRef = ref<HTMLElement>()

const selectedLabel = computed(() => {
    const opt = props.options.find(o => o.value === props.modelValue)
    return opt?.label || props.placeholder
})

function select(value: string) {
    emit('update:modelValue', value)
    emit('change', value)
    isOpen.value = false
}

function handleClickOutside(e: MouseEvent) {
    if (dropdownRef.value && !dropdownRef.value.contains(e.target as Node)) {
        isOpen.value = false
    }
}

onMounted(() => document.addEventListener('click', handleClickOutside))
onBeforeUnmount(() => document.removeEventListener('click', handleClickOutside))
</script>

<template>
    <div ref="dropdownRef" class="relative">
        <button
            type="button"
            @click="isOpen = !isOpen"
            class="flex items-center justify-between gap-2 w-full border border-cc-border-med rounded-lg px-4 py-2.5 text-sm bg-cc-elevated text-cc-text focus:ring-2 focus:ring-cc-primary focus:outline-none transition-colors hover:border-gray-400 dark:hover:border-gray-500"
        >
            <span class="truncate">{{ selectedLabel }}</span>
            <svg
                class="w-4 h-4 shrink-0 text-cc-text-secondary transition-transform"
                :class="{ 'rotate-180': isOpen }"
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
            </svg>
        </button>

        <Transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="opacity-0 -translate-y-1"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 -translate-y-1"
        >
            <ul
                v-if="isOpen"
                class="absolute z-50 mt-1 w-full bg-cc-elevated border border-cc-border rounded-lg shadow-lg py-1 max-h-60 overflow-auto"
            >
                <li
                    v-for="opt in options"
                    :key="opt.value"
                    @click="select(opt.value)"
                    class="px-4 py-2 text-sm cursor-pointer transition-colors flex items-center gap-2"
                    :class="[
                        opt.value === modelValue
                            ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-medium'
                            : 'text-cc-text-secondary hover:bg-cc-hover'
                    ]"
                >
                    <svg
                        v-if="opt.value === modelValue"
                        class="w-4 h-4 shrink-0"
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                    </svg>
                    <span v-else class="w-4 shrink-0"></span>
                    <span class="truncate">{{ opt.label }}</span>
                </li>
            </ul>
        </Transition>
    </div>
</template>
